import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import BookCard from "@/components/BookCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Headphones, PenLine, Sparkles } from "lucide-react";
import { toast } from "sonner";

const testimonials = [
  { name: "Marie L.", text: "Une écriture qui touche l'âme. Chaque page est une invitation à réfléchir.", role: "Lectrice, Paris" },
  { name: "Jean-Pierre M.", text: "Baron Vincent de Paul a une plume incisive et bienveillante. Un auteur incontournable.", role: "Enseignant" },
  { name: "Aïcha D.", text: "Ces livres m'ont accompagnée dans des moments difficiles. Merci.", role: "Lectrice fidèle" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/books", { params: { filter: "new", limit: 3 } })
      .then((r) => setFeatured(r.data.items))
      .catch(() => toast.error("Impossible de charger les livres"));
  }, []);

  return (
    <div className="fade-in">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="label-eyebrow text-accent mb-6">BARON BOOKS — Éditions indépendantes</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-balance">
              Des histoires qui <em className="text-accent not-italic">éveillent</em>, inspirent et transforment.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
              Découvrez des livres qui racontent, questionnent et inspirent. Publiés directement par Baron Vincent de Paul, livrés en un clic dans votre bibliothèque numérique.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/catalogue">
                <Button data-testid="hero-discover-btn" size="lg" className="rounded-sm h-12 px-6">
                  Découvrir les livres <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/catalogue">
                <Button data-testid="hero-buy-btn" variant="outline" size="lg" className="rounded-sm h-12 px-6">
                  Explorer la collection
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { icon: BookOpen, label: "Livres numériques" },
                { icon: Headphones, label: "Livres audio (bientôt)" },
                { icon: Sparkles, label: "Téléchargement instantané" },
              ].map((f) => (
                <div key={f.label} className="text-xs">
                  <f.icon className="w-5 h-5 text-accent mb-2" />
                  <div className="text-muted-foreground leading-tight">{f.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] grid grid-cols-3 gap-4">
              {featured.slice(0, 3).map((b, i) => (
                <div key={b.id} className={`relative ${i === 1 ? "translate-y-8" : ""} ${i === 2 ? "translate-y-4" : ""}`}>
                  <div className="aspect-[2/3] overflow-hidden book-shadow border border-border">
                    <img src={b.cover_url} alt={b.title} className="w-full h-full object-cover" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Nouveautés */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label-eyebrow text-accent mb-3">Les nouveautés</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Fraîchement publiés</h2>
          </div>
          <Link to="/catalogue" className="text-sm text-accent hover:underline hidden md:inline">Voir tout le catalogue →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {featured.map((b, i) => <BookCard key={b.id} book={b} priority={i === 0} />)}
        </div>
      </section>

      {/* Auteur */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-border bg-secondary/40">
              <img
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_bookstore-hub-112/artifacts/olan3ia9_file_0000000034ac820e8a67e14274bd5f7f.png"
                alt="Baron Vincent de Paul"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-accent mb-4">L'auteur</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              Baron Vincent de Paul
            </h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
              Écrivain, théologien et conférencier. À travers ses ouvrages, Baron Vincent de Paul explore les grandes questions de notre temps : la dignité de la femme, le sens du pouvoir, la souffrance et la guérison.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Chacun de ses livres est une invitation à penser, ressentir et se transformer.
            </p>
            <div className="mt-8">
              <Link to="/auteur">
                <Button variant="outline" className="rounded-sm" data-testid="author-more-btn">
                  <PenLine className="w-4 h-4 mr-2" /> En savoir plus
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="label-eyebrow text-accent mb-3">Ils ont lu</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Voix de lecteurs</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t) => (
            <figure key={t.name} className="p-8 border border-border bg-card">
              <blockquote className="font-serif text-lg leading-relaxed italic">"{t.text}"</blockquote>
              <figcaption className="mt-6 pt-6 border-t border-border">
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>
    </div>
  );
}
